import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";

const MAX_BYTES = 10 * 1024 * 1024;

export function useAttachments(max = 4) {
  const [items, setItems] = useState([]);
  const ref = useRef(items);
  ref.current = items;

  useEffect(() => () => ref.current.forEach((x) => URL.revokeObjectURL(x.preview)), []);

  const patch = (key, upd) => setItems((xs) => xs.map((x) => (x.key === key ? { ...x, ...upd } : x)));

  const add = useCallback((fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    const slots = Math.max(0, max - ref.current.length);
    if (files.length > slots) toast.error(`You can attach up to ${max} images per message`);
    files.slice(0, slots).forEach((file) => {
      if (file.size > MAX_BYTES) { toast.error(`${file.name} is larger than 10 MB`); return; }
      const key = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setItems((xs) => [...xs, { key, name: file.name, preview: URL.createObjectURL(file), progress: 0 }]);
      api.upload(file, (p) => patch(key, { progress: p }))
        .then((r) => patch(key, { id: r.id, url: r.url, progress: 100 }))
        .catch((e) => { toast.error(errMsg(e, "Upload failed")); setItems((xs) => xs.filter((x) => x.key !== key)); });
    });
  }, [max]);

  const remove = useCallback((key) => setItems((xs) => xs.filter((x) => x.key !== key)), []);
  const clear = useCallback(() => setItems([]), []);
  const onPaste = useCallback((e) => {
    if (e.clipboardData?.files?.length) { e.preventDefault(); add(e.clipboardData.files); }
  }, [add]);

  return {
    items, add, remove, clear, onPaste,
    ids: items.filter((x) => x.id).map((x) => x.id),
    uploading: items.some((x) => !x.id),
  };
}
